const STICKY_PRODUCT_STORAGE_KEY = 'isbStickyProduct';

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.threshold = 180;
    this.isVisible = false;
    this.buttonInView = true;
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
  }

  connectedCallback() {
    const thresholdAttr = Number.parseInt(this.dataset.threshold || '', 10);
    if (!Number.isNaN(thresholdAttr)) {
      this.threshold = thresholdAttr;
    }

    this.productForm = document.querySelector('.product-form');
    this.variantSelect = this.productForm?.querySelector('.product-variant-select, select[name="id"]');
    this.mainAddToCart = this.productForm?.querySelector('[name="add"]');
    this.quantityInput = this.productForm?.querySelector('[name="quantity"]');
    this.button = this.querySelector('.sticky-atc__button');
    this.titleEl = this.querySelector('.sticky-atc__title');
    this.variantEl = this.querySelector('.sticky-atc__variant');
    this.priceEl = this.querySelector('.sticky-atc__price');
    this.productTitle = this.dataset.productTitle || '';
    this.productUrl = this.dataset.productUrl || window.location.pathname;
    this.productImage = this.dataset.productImage || '';

    if (!this.productForm || !this.mainAddToCart || !this.button) {
      this.remove();
      return;
    }

    this.button.addEventListener('click', this.handleAddToCart);
    this.productForm.addEventListener('change', this.handleFormChange);
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.buttonInView = entries[0]?.isIntersecting ?? false;
          this.updateVisibility();
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this.mainAddToCart);
    }

    this.syncVariant();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleAddToCart);
    this.productForm?.removeEventListener('change', this.handleFormChange);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    this.observer?.disconnect();
  }

  isMobileViewport() {
    return window.innerWidth < 990;
  }

  isPopupOpen() {
    return document.body.classList.contains('is-email-popup-open');
  }

  normalizeProductTitle(value) {
    if (!value) {
      return '';
    }

    let normalized = String(value)
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/[⭐🔥📍💧🌿🌱✨💪🌼🩵🤍🐰⚡]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\(\s+/g, '(')
      .replace(/\s+\)/g, ')')
      .trim();

    if (/best seller duo/i.test(normalized)) {
      return 'BEST SELLER DUO';
    }

    if (/lash lover duo/i.test(normalized)) {
      return 'LASH LOVER DUO';
    }

    if (/clean\s*&\s*shape set/i.test(normalized)) {
      return 'CLEAN & SHAPE SET';
    }

    if (/shampoo holiday bogo|buy 1|get 1 free/i.test(normalized)) {
      return 'SHAMPOO DUO';
    }

    return normalized;
  }

  normalizeProductUrl(value) {
    if (!value) {
      return window.location.pathname;
    }

    try {
      return new URL(value, window.location.origin).pathname;
    } catch (error) {
      return value;
    }
  }

  handleScroll() {
    this.updateOffset();
    this.updateVisibility();
  }

  handleResize() {
    this.updateOffset();
    this.updateVisibility();
  }

  handleFormChange() {
    window.requestAnimationFrame(() => {
      this.syncVariant();
      this.updateVisibility();
    });
  }

  updateVisibility() {
    if (!this.isMobileViewport() || this.isPopupOpen()) {
      this.hide();
      return;
    }

    const passedThreshold = window.scrollY > this.threshold;
    const shouldShow = passedThreshold && !this.buttonInView;

    if (shouldShow) {
      this.show();
    } else {
      this.hide();
    }
  }

  updateOffset() {
    const obstructionSelectors = [
      '.shopify-pc__banner',
      '#shopify-pc__banner',
      '.cc-window',
      '.cookie-bar',
      '[data-cookie-banner]',
      '.intercom-lightweight-app-launcher',
      '.tidio-chat',
      '.chat-widget-launcher'
    ];

    let maxOffset = 0;

    obstructionSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.position !== 'fixed') {
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.height <= 0) {
          return;
        }

        if (rect.bottom >= window.innerHeight - 2) {
          maxOffset = Math.max(maxOffset, rect.height + 12);
        }
      });
    });

    this.style.setProperty('--sticky-atc-bottom-offset', `${maxOffset}px`);
  }

  syncVariant() {
    if (!this.variantSelect) {
      return;
    }

    const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
    if (!selectedOption) {
      return;
    }

    const variantTitle = selectedOption.dataset.variantTitle || '';
    const price = selectedOption.dataset.price || '';
    const available = selectedOption.dataset.available !== 'false';
    const variantId = selectedOption.value;

    const normalizedTitle = this.normalizeProductTitle(this.productTitle);
    const normalizedUrl = this.normalizeProductUrl(this.productUrl);

    if (this.titleEl && normalizedTitle) {
      this.titleEl.textContent = normalizedTitle;
    }

    if (this.variantEl) {
      this.variantEl.textContent = variantTitle && variantTitle !== 'Default Title' ? variantTitle : 'Ready to add';
    }

    if (this.priceEl && price) {
      this.priceEl.textContent = price;
    }

    this.button.disabled = !available;
    this.button.textContent = available ? 'ADD TO CART' : 'SOLD OUT';

    if (variantId) {
      this.persistCurrentProduct({
        productTitle: normalizedTitle,
        productUrl: normalizedUrl,
        image: this.productImage,
        variantId,
        variantTitle: variantTitle && variantTitle !== 'Default Title' ? variantTitle : '',
        price,
        available,
      });
    }
  }

  show() {
    if (this.isVisible) {
      return;
    }

    this.isVisible = true;
    this.classList.add('sticky-atc--visible');
  }

  hide() {
    if (!this.isVisible) {
      return;
    }

    this.isVisible = false;
    this.classList.remove('sticky-atc--visible');
  }

  handleAddToCart() {
    if (!this.mainAddToCart || this.mainAddToCart.disabled) {
      return;
    }

    if (this.quantityInput && this.productForm) {
      const stickyQty = this.querySelector('[data-sticky-quantity]');
      if (stickyQty && stickyQty.value) {
        this.quantityInput.value = stickyQty.value;
      }
    }

    this.mainAddToCart.click();
  }

  persistCurrentProduct(state) {
    try {
      window.localStorage.setItem(
        STICKY_PRODUCT_STORAGE_KEY,
        JSON.stringify({
          ...state,
          updatedAt: Date.now(),
        })
      );
    } catch (error) {
      console.debug('Sticky product persistence skipped', error);
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
